import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import useRepo from '@/nebula/repo';
import useResourceSettings from '@/composables/logged-in-user-account/resource-settings';
import { automaticColor } from 'shared/lib/color';
import { computed, ref } from 'vue';
import { findInArray } from '@/lib/array';
import { optionColor } from 'shared/constants.json';
import { sortByArray } from '@/lib/sort';
import {
  space as spaceConfig,
} from 'shared/api/query/configs.json';

export default function useSpaces(search) {
  const repo = useRepo(spaceConfig.model);

  const { loggedInUserAccount } = useLoggedInUserAccount();
  const { resourceSettings } = useResourceSettings();

  const sortedSpaces = computed(() => (resourceSettings.value === undefined ? repo.entityList.value : repo.entityList.value.sort(sortByArray(resourceSettings.value.spaceOrder))));

  const isMatch = (node, searchTerm) => node.title.toLowerCase().includes(searchTerm.toLowerCase());
  const hasMatch = (res, node) => node.match || res || node.children.reduce(hasMatch, false);

  const treeBuilder = (spaces) => {
    const mapChildMatches = (node) => ({
      ...node,
      containsMatch: node.children.reduce(hasMatch, false),
      children: node.children.map(mapChildMatches),
    });

    const mapChildren = (level, parentCollapsed) => (c) => {
      const node = findInArray({ haystack: spaces, needle: c.uid });
      if (node === null) {
        return c;
      }

      const children = spaces.filter((s) => s.parents.filter((p) => p.uid === node.uid).length > 0);
      const collapsed = collapsedNodes.value.includes(node.uid);
      return {
        ...node,
        match: isMatch(node, search.value),
        children: children.map(mapChildren(level + 1, collapsed || parentCollapsed)),
        collapsed,
        parentCollapsed,
        level,
      };
    };
    return spaces.filter((o) => o.parents.length === 0)
      .map(mapChildren(0, false))
      .map(mapChildMatches);
  };

  const collapsedNodes = ref([]);
  const toggleCollapse = (uid) => {
    if (collapsedNodes.value.includes(uid)) {
      collapsedNodes.value = collapsedNodes.value.filter((e) => e !== uid);
      return;
    }
    collapsedNodes.value.push(uid);
  };

  const depthFirstOrder = (res, node) => [...res, node, ...node.children.reduce(depthFirstOrder, [])];
  const tree = computed(() => treeBuilder(sortedSpaces.value));
  const normalizedSpaces = computed(() => applyMappers().reduce(depthFirstOrder, []));

  const mappers = ref([]);
  const setSpaceMappers = (array) => {
    mappers.value = array;
  };

  const applyMappers = () => {
    let spaces = tree.value;
    mappers.value.forEach((mapper) => {
      spaces = spaces.map(mapper);
    });
    return spaces;
  };

  const filteredSpaces = computed(() => normalizedSpaces.value.filter((s) => s.match === true));

  const colorfulSpaces = computed(() => sortedSpaces.value.map((s) => {
    if (s.color === null) {
      return {
        ...s,
        color: automaticColor(s.title, optionColor.all),
      };
    }

    return s;
  }));

  const createSpace = (space) => repo.createSingle({
    ...space,
    account: { uid: loggedInUserAccount.value.uid },
  });

  const deleteSpace = (space) => repo.deleteSingle(space.uid);

  return {
    getList: repo.getList,
    selectSingle: repo.selectSingle,
    selectMultiple: repo.selectMultiple,
    createSpace,
    updateSpace: repo.updateSingle,
    deleteSpace,
    spaces: sortedSpaces,

    colorfulSpaces,
    normalizedSpaces,
    filteredSpaces,
    setSpaceMappers,
    toggleCollapse,
  };
}
